import React, { useImperativeHandle, useState, useRef, useContext } from "react";
import { Popup, PopupRef, Position, ToolbarItem } from 'devextreme-react/popup';
import 'devextreme-react/text-area';
import Form, { ButtonItem, EmptyItem, FormRef, GroupItem, Item, Label, SimpleItem } from 'devextreme-react/form';
import { AssistitiApi, OperatoriApi, SendUserInviteDTO, UsersApi } from "../../data";
import { ClickEvent } from "devextreme/ui/button";
import { apiConfiguration } from "../../apiConfig";
import notify from "devextreme/ui/notify";
import TenantContext from "../../contexts/tenantContext";
import { useRolesDataSource } from "../../datasources/RolesDataSource";
import { showError } from "../../utils/errorHandling";
import { LoadIndicator } from "devextreme-react";


type Props = {}

export type SendInvitePopupHandle = {
  inviteUser: () => void,
  inviteAssistito: (assistitoId: string) => void,
  inviteOperatore: (operatoreId: string) => void,
}

type state = {
  genericInvite: boolean,
  visible: boolean,
  title: string
}

const PopupSendInvite: React.ForwardRefRenderFunction<SendInvitePopupHandle, Props> = (props, ref) => {
  const { currentTenant } = useContext(TenantContext);
  const [state, setState] = useState<state>({ genericInvite: false, visible: false, title: "" });
  const formRef = useRef<FormRef>(null);
  const [formData, setFormData] = useState<any>();
  const [waiting, setWaiting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const roles = useRolesDataSource();

  function hideInfo() {
    setFormData(null);
    setState({ genericInvite: false, visible: false, title: "" });
  }

  useImperativeHandle(ref, () => ({
    async inviteUser() {
      setState({
        genericInvite: true,
        visible: true,
        title: "Invita un nuovo utente"
      });
    },
    async inviteAssistito(assistitoId) {
      setLoading(true);

      setState({
        genericInvite: false,
        visible: true,
        title: "Invia invito a un assistito"
      });

      var api = new AssistitiApi(apiConfiguration);

      var operatore = await api.getAssistitoById({
        tenantId: currentTenant.Id,
        assistitoId: assistitoId
      });

      setFormData({
        nome: operatore.nome,
        cognome: operatore.cognome,
        email: operatore.email,
        assistitoId: assistitoId
      });

      setLoading(false);
    },
    async inviteOperatore(operatoreId) {
      setLoading(true);

      setState({
        genericInvite: false,
        visible: true,
        title: "Invia invito a un operatore"
      });

      var api = new OperatoriApi(apiConfiguration);

      var operatore = await api.getOperatoreById({
        tenantId: currentTenant.Id,
        operatoreId: operatoreId
      });

      setFormData({
        nome: operatore.nome,
        cognome: operatore.cognome,
        email: operatore.email,
        operatoreId: operatoreId
      });

      setLoading(false);
    }
  }));

  function send_click(e: ClickEvent) {
    const api = new UsersApi(apiConfiguration);

    var validate = formRef.current?.instance().validate();
    if (validate?.isValid) {
      var data = formRef.current?.instance().option("formData");
      data = {
        ...data,
        ruoliProposti: data.ruolo ? [data.ruolo] : []
      } as SendUserInviteDTO;

      setWaiting(true);
      api.sendUserInvite({
        tenantId: currentTenant.Id,
        sendUserInviteDTO: data
      }).then(() => {
        notify("Invito inviato correttamente", "success", 3000);
        hideInfo();
      }).catch((error) => {
        showError(error);
      }).finally(() => {
        setWaiting(false);
      });
    }
  }

  return (<React.Fragment>
    {state.visible &&
      <Popup
        visible={true}
        dragEnabled={false}
        hideOnOutsideClick={true}
        showCloseButton={true}
        fullScreen={false}
        showTitle={true}
        onHidden={hideInfo}
        title={state.title}
        container=".dx-viewport"
        width={500}
        height={300}
        maxWidth="90%"
      >
        <Position
          at="center"
          my="center"
          of=""
          collision="fit"
        />
        <ToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          visible={!loading}
          options={{
            text: 'Invia',
            type: 'default',
            stylingMode: 'contained',
            onClick: send_click
          }}
        />
        <ToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={{
            text: 'Annulla',
            type: 'normal',
            stylingMode: 'contained',
            onClick: hideInfo
          }}
        />

        {loading && <LoadIndicator width={32} height={32} visible={true} />}

        {!loading &&
          <Form id="form" ref={formRef} colCount={2} formData={formData} labelMode='floating' >

            <Item dataField="nome" isRequired={true} disabled={!state.genericInvite} />
            <Item dataField="cognome" isRequired={true} disabled={!state.genericInvite} />
            <Item dataField="email" colSpan={2} isRequired={true} disabled={!state.genericInvite} />

            {state.genericInvite &&
              <Item dataField="ruolo" editorType="dxSelectBox" isRequired={false} editorOptions={{ dataSource: roles, displayExpr: 'description', valueExpr: 'id' }} colSpan={2}>
                <Label text="Ruolo" />
              </Item>
            }

          </Form>
        }
      </Popup>
    }
  </React.Fragment>
  );
}

export default React.forwardRef(PopupSendInvite);
